import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { stopPersistedSession } from "@/lib/query-cache";
import { disconnectChatSocket } from "@/lib/chat-socket";

export const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  withCredentials: true,
});

const plainAxios = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  withCredentials: true,
});

export interface RetryableRequest extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}[] = [];

const processQueue = (error: AxiosError | null, response?: any) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(response);
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequest;

    const is401 = error.response?.status === 401;
    const isRefresh = originalRequest.url?.includes("/auth/refresh");
    const isLoginOrLogout =
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/logout");

    if (is401 && !originalRequest._retry && !isRefresh && !isLoginOrLogout) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axiosInstance(originalRequest));
      }

      isRefreshing = true;

      try {
        await axiosInstance.post("/auth/refresh");
        processQueue(null);
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError);

        try {
          await plainAxios.post("/auth/logout");
        } catch {}

        if (typeof window !== "undefined") {
          try {
            const [{ useAccessStore }, { useChatStore }] = await Promise.all([
              import("@/store/access-store"),
              import("@/store/chat-store"),
            ]);
            disconnectChatSocket();
            useChatStore.getState().reset();
            useAccessStore.getState().resetAccess();
          } catch {}
          stopPersistedSession();
          const path = window.location.pathname || "";
          const isPublicAuthPage =
            path.startsWith("/login") ||
            path.startsWith("/forgot-password") ||
            path.startsWith("/reset-password") ||
            path.startsWith("/check-email") ||
            path.startsWith("/accept-invite") ||
            path.startsWith("/sign-change-order") ||
            path.startsWith("/about-us");
          if (!isPublicAuthPage) {
            window.location.replace("/login");
          }
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
